import { create } from "zustand";
import { getSiteIdFromUrl } from "./syncStore";

/**
 * "Don't show the sync reminder again for this site" — a per-site,
 * per-browser-session preference (SUP-924).
 *
 * Every save on the About page and the camera-level edit views re-shows a
 * "please sync" banner. Operators working through a freshly onboarded site
 * (cameras connect, then a run of ignore zones, line crossings, …) asked for
 * a way to stop seeing it. This store remembers that choice:
 *
 *  - Per site, keyed by the site id in the URL — hiding the reminder on one
 *    site never hides it on another. The operator ticks the box again on the
 *    next site if they want it there too.
 *  - Mirrored in sessionStorage, so it survives route changes and reloads but
 *    is dropped when the tab closes — the reminder comes back next visit.
 *  - Cleared on logout (handleLogout), so the next person to sign in on the
 *    same tab gets the reminder again.
 *
 * It hides only the banners. The red "Sync Site" buttons (About page header
 * and edit-view headers) keep showing whenever the site has unsynced changes,
 * so the operator always has a visible way to see and act on pending changes.
 */

const STORAGE_KEY = "syncBannerHiddenSiteIds";

const readMirror = (): string[] => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(stored) ? stored.map(String) : [];
  } catch {
    return [];
  }
};

const writeMirror = (siteIds: string[]) => {
  try {
    if (siteIds.length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(siteIds));
    }
  } catch {
    // Storage unavailable (private mode, quota) — in-memory state still works
    // for this page load.
  }
};

interface SyncBannerPreferenceStore {
  hiddenSiteIds: string[];
  hideSyncBannersForSite: (siteId: string) => void;
  resetSyncBannerPreference: () => void;
}

export const useSyncBannerPreferenceStore = create<SyncBannerPreferenceStore>(
  (set, get) => ({
    hiddenSiteIds: readMirror(),
    hideSyncBannersForSite: (siteId: string) => {
      if (!siteId) return;
      const ids = get().hiddenSiteIds;
      if (ids.includes(siteId)) return;
      const next = [...ids, siteId];
      writeMirror(next);
      set({ hiddenSiteIds: next });
    },
    resetSyncBannerPreference: () => {
      writeMirror([]);
      set({ hiddenSiteIds: [] });
    },
  })
);

/** Reactive hook: has the operator hidden the sync banners for the site in the URL? */
export const useSyncBannersHiddenForCurrentSite = (): boolean =>
  useSyncBannerPreferenceStore((s) =>
    s.hiddenSiteIds.includes(getSiteIdFromUrl())
  );

/** Operator ticked "Don't show again for this site" on a sync banner. */
export const hideSyncBannersForCurrentSite = () =>
  useSyncBannerPreferenceStore
    .getState()
    .hideSyncBannersForSite(getSiteIdFromUrl());

/** Logout / new session — the reminder should show again everywhere. */
export const resetSyncBannerPreference = () =>
  useSyncBannerPreferenceStore.getState().resetSyncBannerPreference();

/** Label shared by both banners so the wording can't drift. */
export const HIDE_SYNC_BANNER_LABEL = "Don't show again for this site";
